import { useState } from "react";
import { toast } from "sonner";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { FreelanceRatesPanel } from "@/components/freelances/FreelanceRatesPanel";
import { FreelanceDossiersPanel } from "@/components/freelances/FreelanceDossiersPanel";
import { FreelancePayoutsPanel } from "@/components/freelances/FreelancePayoutsPanel";
import { BillingSettingsPanel } from "@/components/freelances/BillingSettingsPanel";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { trpc } from "@/lib/trpc";
import { formatDateTime } from "@/lib/utils";
import { AlertCircle, Copy, KeyRound, Mail, Plus, Repeat, UserCheck, UserX, X } from "lucide-react";

// Lot A (10/2026) — comptes FREELANCES de prospection (SDR / Sales), payés à la
// performance. Création UNIQUEMENT par invitation e-mail : l'admin ne choisit
// jamais le mot de passe ; le freelance le pose via un lien à usage unique
// (7 jours) puis active la double authentification (obligatoire).
// Endpoints : admin.freelances.* (SUPER_ADMIN), tous audités.

type FreelanceRole = "FREELANCE_SDR" | "FREELANCE_SALES";

const ROLE_LABELS: Record<string, string> = {
  FREELANCE_SDR: "SDR (prise de RDV)",
  FREELANCE_SALES: "Sales (démos)",
};
const STATUS: Record<string, { label: string; variant: "default" | "secondary" | "outline" | "warning" }> = {
  ACTIVE: { label: "Actif", variant: "default" },
  INVITED: { label: "Invitation envoyée", variant: "warning" },
  DEACTIVATED: { label: "Désactivé", variant: "outline" },
};

function displayName(f: { firstName: string | null; lastName: string | null; email: string }) {
  return [f.firstName, f.lastName].filter(Boolean).join(" ") || f.email;
}

function InviteResult({ url, emailSent, onClose }: { url: string; emailSent: boolean; onClose: () => void }) {
  return (
    <div className="space-y-3">
      <p className="text-sm">
        {emailSent
          ? "Invitation envoyée par e-mail."
          : "L'e-mail n'a pas pu partir (service d'envoi indisponible) — transmettez ce lien vous-même :"}
      </p>
      <div className="flex gap-2">
        <Input readOnly value={url} className="font-mono text-xs" />
        <Button
          type="button"
          variant="outline"
          onClick={() => {
            void navigator.clipboard?.writeText(url);
            toast.success("Lien copié");
          }}
          aria-label="Copier le lien"
        >
          <Copy className="h-4 w-4" />
        </Button>
      </div>
      <p className="text-xs text-muted-foreground">Lien personnel, à usage unique, valable 7 jours.</p>
      <Button className="w-full" onClick={onClose}>Fermer</Button>
    </div>
  );
}

function InviteModal({ onClose, onSuccess }: { onClose: () => void; onSuccess: () => void }) {
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [staffRole, setStaffRole] = useState<FreelanceRole>("FREELANCE_SDR");
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{ url: string; emailSent: boolean } | null>(null);

  const invite = trpc.admin.freelances.invite.useMutation({
    onSuccess: (r) => {
      setResult({ url: r.inviteUrl, emailSent: r.emailSent });
      onSuccess();
    },
    onError: (err) => setError(err.message),
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-background border rounded-lg shadow-lg w-full max-w-md p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Mail className="h-4 w-4 text-primary" /> Inviter un freelance
          </h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground" aria-label="Fermer">
            <X className="h-4 w-4" />
          </button>
        </div>
        {result ? (
          <InviteResult url={result.url} emailSent={result.emailSent} onClose={onClose} />
        ) : (
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              setError(null);
              invite.mutate({ email, staffRole, firstName: firstName || undefined, lastName: lastName || undefined });
            }}
          >
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="fl-first">Prénom</Label>
                <Input id="fl-first" value={firstName} onChange={(e) => setFirstName(e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="fl-last">Nom</Label>
                <Input id="fl-last" value={lastName} onChange={(e) => setLastName(e.target.value)} />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="fl-email">E-mail *</Label>
              <Input id="fl-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="fl-role">Rôle *</Label>
              <select
                id="fl-role"
                value={staffRole}
                onChange={(e) => setStaffRole(e.target.value as FreelanceRole)}
                className="w-full h-9 rounded-md border border-input bg-background px-3 py-1 text-sm"
              >
                <option value="FREELANCE_SDR">{ROLE_LABELS.FREELANCE_SDR}</option>
                <option value="FREELANCE_SALES">{ROLE_LABELS.FREELANCE_SALES}</option>
              </select>
            </div>
            <p className="text-xs text-muted-foreground">
              Le freelance choisira son mot de passe via le lien reçu, puis activera la double authentification.
              Il n'aura accès qu'à l'espace prospection de l'app OPS.
            </p>
            {error && (
              <div className="flex items-center gap-2 text-sm text-destructive">
                <AlertCircle className="h-4 w-4" /> {error}
              </div>
            )}
            <div className="flex gap-2">
              <Button type="button" variant="outline" className="flex-1" onClick={onClose}>Annuler</Button>
              <Button type="submit" className="flex-1" disabled={invite.isLoading}>
                {invite.isLoading ? "Envoi…" : "Envoyer l'invitation"}
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

export default function Freelances() {
  const utils = trpc.useUtils();
  const list = trpc.admin.freelances.list.useQuery();
  const [inviteOpen, setInviteOpen] = useState(false);
  const [resent, setResent] = useState<{ url: string; emailSent: boolean } | null>(null);
  const [reassignFrom, setReassignFrom] = useState("");
  const [reassignTo, setReassignTo] = useState("");

  const refresh = () => void utils.admin.freelances.list.invalidate();
  const setActive = trpc.admin.freelances.setActive.useMutation({
    onSuccess: (r) => {
      toast.success(r.active ? "Compte réactivé" : `Compte désactivé — ${r.revokedTokens} session(s) révoquée(s)`);
      refresh();
    },
    onError: (e) => toast.error(e.message),
  });
  const resend = trpc.admin.freelances.resendInvite.useMutation({
    onSuccess: (r) => {
      setResent({ url: r.inviteUrl, emailSent: r.emailSent });
      refresh();
    },
    onError: (e) => toast.error(e.message),
  });
  const reset2FA = trpc.admin.freelances.reset2FA.useMutation({
    onSuccess: (r) => {
      toast.success(`Double authentification réinitialisée — ${r.revokedTokens} session(s) révoquée(s). Ré-enrôlement à la prochaine connexion.`);
      refresh();
    },
    onError: (e) => toast.error(e.message),
  });
  const reassign = trpc.admin.freelances.reassignPortfolio.useMutation({
    onSuccess: (r) => {
      toast.success(`Portefeuille réaffecté : ${Object.entries(r).map(([k, v]) => `${v} ${k}`).join(", ")}`);
      setReassignFrom("");
      setReassignTo("");
      refresh();
    },
    onError: (e) => toast.error(e.message),
  });

  const rows = list.data ?? [];
  const from = rows.find((r) => r.id === reassignFrom);
  const targets = rows.filter((r) => r.id !== reassignFrom && r.status === "ACTIVE" && from && r.staffRole === from.staffRole);

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <UserCheck className="h-6 w-6" /> Freelances prospection
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              SDR et Sales externes. Accès limité à l'espace prospection, double authentification obligatoire.
            </p>
          </div>
          <Button onClick={() => setInviteOpen(true)}>
            <Plus className="h-4 w-4 mr-2" /> Inviter un freelance
          </Button>
        </div>

        <Card>
          <CardContent className="pt-6 overflow-x-auto">
            {list.isLoading ? (
              <Skeleton className="h-24 w-full" />
            ) : rows.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-6">Aucun freelance pour le moment.</p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-muted-foreground border-b">
                    <th className="py-2 pr-3">Freelance</th>
                    <th className="py-2 pr-3">Rôle</th>
                    <th className="py-2 pr-3">Statut</th>
                    <th className="py-2 pr-3">Dernière connexion</th>
                    <th className="py-2 pr-3">Prospects</th>
                    <th className="py-2 pr-3">RDV (tenus)</th>
                    <th className="py-2" />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((f) => {
                    const st = STATUS[f.status] ?? { label: f.status, variant: "outline" as const };
                    return (
                      <tr key={f.id} className="border-b last:border-0">
                        <td className="py-2 pr-3">
                          <div className="font-medium">{displayName(f)}</div>
                          <div className="text-xs text-muted-foreground">{f.email}</div>
                        </td>
                        <td className="py-2 pr-3">{ROLE_LABELS[f.staffRole ?? ""] ?? f.staffRole}</td>
                        <td className="py-2 pr-3">
                          <Badge variant={st.variant}>{st.label}</Badge>
                          {f.status === "ACTIVE" && !f.totpEnabled && (
                            <div className="text-xs text-muted-foreground mt-0.5">2FA à activer</div>
                          )}
                        </td>
                        <td className="py-2 pr-3 text-xs">{f.lastLoginAt ? formatDateTime(f.lastLoginAt) : "—"}</td>
                        <td className="py-2 pr-3">{f.assignedProspects}</td>
                        <td className="py-2 pr-3">{f.meetingsTotal} ({f.meetingsHonored})</td>
                        <td className="py-2 text-right whitespace-nowrap">
                          {f.status === "INVITED" && (
                            <Button size="sm" variant="ghost" disabled={resend.isLoading} onClick={() => resend.mutate({ userId: f.id })}>
                              <Mail className="h-4 w-4 mr-1" /> Renvoyer
                            </Button>
                          )}
                          {f.totpEnabled && (
                            <Button size="sm" variant="ghost" disabled={reset2FA.isLoading}
                              title="Téléphone perdu : efface la 2FA et force le ré-enrôlement"
                              onClick={() => reset2FA.mutate({ userId: f.id })}>
                              <KeyRound className="h-4 w-4 mr-1" /> Réinit. 2FA
                            </Button>
                          )}
                          {f.status === "DEACTIVATED" ? (
                            <Button size="sm" variant="ghost" disabled={setActive.isLoading} onClick={() => setActive.mutate({ userId: f.id, active: true })}>
                              <UserCheck className="h-4 w-4 mr-1" /> Réactiver
                            </Button>
                          ) : (
                            <Button size="sm" variant="ghost" disabled={setActive.isLoading} onClick={() => setActive.mutate({ userId: f.id, active: false })}>
                              <UserX className="h-4 w-4 mr-1" /> Désactiver
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>

        {/* Lot C — barème personnel par freelance (surcharge du barème global). */}
        {/* Espace freelance pro (05/10/2026) : versements à virer, dossiers et cagnottes. */}
        <FreelancePayoutsPanel />
        <BillingSettingsPanel />
        <FreelanceDossiersPanel />
        <FreelanceRatesPanel />

        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Repeat className="h-4 w-4" /> Réaffecter un portefeuille
            </CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
            <div className="space-y-1.5">
              <Label htmlFor="ra-from">De</Label>
              <select id="ra-from" className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
                value={reassignFrom} onChange={(e) => { setReassignFrom(e.target.value); setReassignTo(""); }}>
                <option value="">Choisir…</option>
                {rows.map((f) => <option key={f.id} value={f.id}>{displayName(f)} — {ROLE_LABELS[f.staffRole ?? ""]}</option>)}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ra-to">Vers</Label>
              <select id="ra-to" className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
                value={reassignTo} onChange={(e) => setReassignTo(e.target.value)}>
                <option value="">Choisir…</option>
                {targets.map((f) => <option key={f.id} value={f.id}>{displayName(f)}</option>)}
              </select>
            </div>
            <Button disabled={!reassignFrom || !reassignTo || reassign.isLoading}
              onClick={() => reassign.mutate({ fromUserId: reassignFrom, toUserId: reassignTo })}>
              Réaffecter
            </Button>
            <p className="text-xs text-muted-foreground sm:col-span-3">
              SDR : établissements assignés + appels planifiés (les RDV déjà posés restent attribués au SDR d'origine).
              Sales : opportunités ouvertes + RDV à venir.
            </p>
          </CardContent>
        </Card>
      </div>

      {inviteOpen && <InviteModal onClose={() => setInviteOpen(false)} onSuccess={refresh} />}
      {resent && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-background border rounded-lg shadow-lg w-full max-w-md p-6">
            <InviteResult url={resent.url} emailSent={resent.emailSent} onClose={() => setResent(null)} />
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
